// Goals engine (Goals & Coach spec, Phase 1). Pure and deterministic: every number on the Goals
// tab and the Home goal blocks comes from here, never from an AI (spec principle 1).
//
// Back-solve per revenue stream (one per role):
//   income share ÷ net per deal = deals needed ÷ each step's conversion rate … = meetings ÷ call→meeting = calls
// Every rate is an editable "starting assumption" — placeholders, not industry benchmarks.

export type Role = 'investment_sales' | 'll_rep' | 'tenant_rep' | 'acquisition' | 'buyside';
export type Experience = 'lt1' | '1to3' | '3to10' | '10plus';

/** A funnel step after meetings. `metric` groups steps across roles on Home (e.g. listings). */
export interface StepTemplate {
  metric: Metric;
  label: string;
  /** Deal type + stage keys whose entry counts as this step happening (from deal_stage_history). */
  stages: { dealType: string; stage: string }[];
  /** Default conversion from the previous step (meetings for the first step). */
  rate: number;
}

export type Metric = 'calls' | 'meetings' | 'bovs' | 'listings' | 'engagements' | 'under_contract' | 'deals_done' | 'revenue';

/** Lower-case plural for sentences ("31 BOVs by month 6"). */
export const METRIC_NOUN: Record<Metric, string> = {
  calls: 'calls',
  meetings: 'meetings',
  bovs: 'BOVs',
  listings: 'listings',
  engagements: 'clients engaged',
  under_contract: 'deals under contract',
  deals_done: 'deals done',
  revenue: 'revenue',
};
const n0 = (n: number) => Math.round(n).toLocaleString('en-US');

export const METRIC_LABEL: Record<Metric, string> = {
  calls: 'Calls',
  meetings: 'Meetings',
  bovs: 'BOVs',
  listings: 'Listings',
  engagements: 'Clients engaged',
  under_contract: 'Under contract',
  deals_done: 'Deals done',
  revenue: 'Revenue',
};

/** Funnel per role (approved by Brett 2026-10-22). The last step is the deal that pays. */
export const ROLE_TEMPLATES: Record<Role, { label: string; dealType: string; steps: StepTemplate[]; horizonBase: number }> = {
  investment_sales: {
    label: 'Investment sales',
    dealType: 'listing_sale',
    horizonBase: 21,
    steps: [
      { metric: 'bovs', label: 'BOVs', stages: [{ dealType: 'listing_sale', stage: 'bov' }], rate: 0.25 },
      { metric: 'listings', label: 'Listings', stages: [{ dealType: 'listing_sale', stage: 'listing_marketing' }], rate: 0.25 },
      { metric: 'deals_done', label: 'Sales closed', stages: [{ dealType: 'listing_sale', stage: 'closed' }], rate: 0.6 },
    ],
  },
  ll_rep: {
    label: 'Landlord rep leasing',
    dealType: 'll_rep',
    horizonBase: 15,
    steps: [
      { metric: 'listings', label: 'Listings', stages: [{ dealType: 'll_rep', stage: 'listing' }], rate: 0.15 },
      { metric: 'deals_done', label: 'Leases signed', stages: [{ dealType: 'll_rep', stage: 'lease_signed' }], rate: 0.6 },
    ],
  },
  tenant_rep: {
    label: 'Tenant rep leasing',
    dealType: 'tenant_rep',
    horizonBase: 15,
    steps: [
      { metric: 'engagements', label: 'Clients engaged', stages: [{ dealType: 'tenant_rep', stage: 'search' }], rate: 0.2 },
      { metric: 'deals_done', label: 'Leases signed', stages: [{ dealType: 'tenant_rep', stage: 'lease_signed' }], rate: 0.6 },
    ],
  },
  acquisition: {
    label: 'Acquisitions',
    dealType: 'acquisition',
    horizonBase: 15,
    steps: [
      { metric: 'engagements', label: 'Clients engaged', stages: [{ dealType: 'acquisition', stage: 'search' }], rate: 0.2 },
      { metric: 'under_contract', label: 'Under contract', stages: [{ dealType: 'acquisition', stage: 'under_contract' }], rate: 0.4 },
      { metric: 'deals_done', label: 'Closings', stages: [{ dealType: 'acquisition', stage: 'closed' }], rate: 0.8 },
    ],
  },
  buyside: {
    label: 'Buyside',
    dealType: 'buyside',
    horizonBase: 15,
    steps: [
      { metric: 'engagements', label: 'Clients engaged', stages: [{ dealType: 'buyside', stage: 'search' }], rate: 0.2 },
      { metric: 'under_contract', label: 'Under contract', stages: [{ dealType: 'buyside', stage: 'under_contract' }], rate: 0.4 },
      { metric: 'deals_done', label: 'Closings', stages: [{ dealType: 'buyside', stage: 'closed' }], rate: 0.8 },
    ],
  },
};
export const ROLES = Object.keys(ROLE_TEMPLATES) as Role[];

/** Starting call→meeting rate by experience (placeholder assumptions, editable). */
export const DEFAULT_CALL_TO_MEETING: Record<Experience, number> = { lt1: 0.015, '1to3': 0.02, '3to10': 0.025, '10plus': 0.03 };

export interface StreamInput {
  role: Role;
  /** Share of the income goal this stream should produce (0–1). Shares are normalized to sum to 1. */
  share: number;
  dealSize: number;
  /** 'pct': fee = dealSize × feePct; 'amount': fee = feeAmount per deal. */
  feeMode: 'pct' | 'amount';
  feePct: number;
  feeAmount: number;
  callToMeeting: number;
  /** Conversion per step, same order as the role template. */
  rates: number[];
}

export interface PlanInput {
  incomeGoal: number;
  /** Your share after the house (0–1), and after any co-broker / team split (0–1). */
  houseSplit: number;
  coBrokerSplit: number;
  workingWeeks: number;
  prospectingHoursPerWeek: number | null;
  callsPerHour: number;
  secondJob: boolean;
  streams: StreamInput[];
}

export interface StreamPlan {
  role: Role;
  label: string;
  share: number;
  grossFeePerDeal: number;
  netPerDeal: number;
  incomeTarget: number;
  /** Annual counts, from deals done back to calls. */
  calls: number;
  meetings: number;
  steps: { metric: Metric; label: string; annual: number; rate: number }[];
}

export interface Plan {
  streams: StreamPlan[];
  /** Annual totals per metric across streams (revenue = income goal). */
  annual: Record<Metric, number>;
  perWeek: Record<Metric, number>;
  perMonth: Record<Metric, number>;
  realism: { requiredCallsPerWeek: number; capacityPerWeek: number | null; ok: boolean };
}

const EMPTY = (): Record<Metric, number> => ({ calls: 0, meetings: 0, bovs: 0, listings: 0, engagements: 0, under_contract: 0, deals_done: 0, revenue: 0 });

export function feePerDeal(s: Pick<StreamInput, 'dealSize' | 'feeMode' | 'feePct' | 'feeAmount'>): number {
  return s.feeMode === 'amount' ? s.feeAmount : s.dealSize * s.feePct;
}

export function backSolve(input: PlanInput): Plan {
  const shareTotal = input.streams.reduce((t, s) => t + Math.max(0, s.share), 0) || 1;
  const annual = EMPTY();
  annual.revenue = input.incomeGoal;
  const streams = input.streams.map((s): StreamPlan => {
    const tpl = ROLE_TEMPLATES[s.role];
    const share = Math.max(0, s.share) / shareTotal;
    const gross = feePerDeal(s);
    const net = gross * input.houseSplit * input.coBrokerSplit;
    const incomeTarget = input.incomeGoal * share;
    const deals = net > 0 ? incomeTarget / net : 0;
    // Walk backwards: each step needs (next step ÷ next step's rate).
    const counts: number[] = new Array(tpl.steps.length);
    let next = deals;
    for (let i = tpl.steps.length - 1; i >= 0; i--) {
      counts[i] = next;
      const rate = s.rates[i] ?? tpl.steps[i]!.rate;
      next = rate > 0 ? next / rate : 0;
    }
    const meetings = next;
    const calls = s.callToMeeting > 0 ? meetings / s.callToMeeting : 0;
    const steps = tpl.steps.map((st, i) => ({ metric: st.metric, label: st.label, annual: counts[i]!, rate: s.rates[i] ?? st.rate }));
    annual.calls += calls;
    annual.meetings += meetings;
    for (const st of steps) annual[st.metric] += st.annual;
    return { role: s.role, label: tpl.label, share, grossFeePerDeal: gross, netPerDeal: net, incomeTarget, calls, meetings, steps };
  });
  const weeks = input.workingWeeks > 0 ? input.workingWeeks : 48;
  const perWeek = EMPTY();
  const perMonth = EMPTY();
  for (const k of Object.keys(annual) as Metric[]) {
    perWeek[k] = annual[k] / weeks;
    perMonth[k] = annual[k] / 12;
  }
  const hours = input.prospectingHoursPerWeek;
  const capacity = hours == null ? null : hours * input.callsPerHour;
  return { streams, annual, perWeek, perMonth, realism: { requiredCallsPerWeek: perWeek.calls, capacityPerWeek: capacity, ok: capacity == null || perWeek.calls <= capacity } };
}

// ---------------------------------------------------------------------------
// Urgency (spec 4.2): changes tone and ramp, never targets. Private to the user.
// ---------------------------------------------------------------------------

export interface UrgencyAnswers {
  support: 'none' | 'some' | 'substantial';
  savings: 'none' | 'lt6' | '6to12' | '12plus' | null;
  passiveIncome: boolean | null;
  secondJob: boolean | null;
  familyCovers: boolean | null;
  connections: boolean | null;
}
export type UrgencyTier = 'low' | 'medium' | 'high';

export function urgencyTier(a: UrgencyAnswers): UrgencyTier {
  let score = { none: 2, some: 1, substantial: 0 }[a.support];
  score += a.savings ? { none: 2, lt6: 1, '6to12': 0.5, '12plus': 0 }[a.savings] : 1;
  if (a.passiveIncome) score -= 1;
  if (a.familyCovers) score -= 1;
  if (a.secondJob) score -= 0.5;
  if (a.connections) score -= 0.5;
  return score >= 3 ? 'high' : score >= 1.5 ? 'medium' : 'low';
}

/**
 * Months until meaningful income (spec 5.4): 15 by default, 21 for investment sales; shorter with
 * experience, an existing pipeline or connections. Editable.
 */
export function horizonMonths(roles: Role[], experience: Experience, hasPipeline: boolean, connections: boolean): number {
  const base = Math.max(...roles.map((r) => ROLE_TEMPLATES[r].horizonBase), 15);
  const exp = { lt1: 0, '1to3': 3, '3to10': 9, '10plus': 12 }[experience];
  return Math.max(0, base - exp - (hasPipeline ? 3 : 0) - (connections ? 2 : 0));
}

// ---------------------------------------------------------------------------
// Milestones (spec 5.4 / 6.1): leading indicators first, then funnel steps, first closing at the horizon.
// ---------------------------------------------------------------------------

export interface MilestoneDraft {
  dueDate: string;
  metric: Metric;
  target: number;
  label: string;
}

const addMonths = (iso: string, m: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + m);
  return d.toISOString().slice(0, 10);
};
const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export function generateMilestones(plan: Plan, start: string, horizon: number): MilestoneDraft[] {
  const out: MilestoneDraft[] = [];
  const cum = (metric: Metric, months: number) => Math.round(plan.perMonth[metric] * months);
  // Activity at full pace from day one (the leading indicator).
  for (const [days, months] of [[30, 1], [90, 3]] as const) {
    if (plan.annual.calls > 0) out.push({ dueDate: addDays(start, days), metric: 'calls', target: cum('calls', months), label: `${n0(cum('calls', months))} calls by day ${days}` });
    if (plan.annual.meetings > 0) out.push({ dueDate: addDays(start, days), metric: 'meetings', target: Math.max(1, cum('meetings', months)), label: `${n0(Math.max(1, cum('meetings', months)))} meetings by day ${days}` });
  }
  // Middle steps start a quarter in; the paying deal lands at the horizon.
  const middle: Metric[] = ['bovs', 'engagements', 'listings', 'under_contract'];
  const steps = middle.filter((m) => plan.annual[m] > 0);
  steps.forEach((m, i) => {
    const months = Math.max(3, Math.round(((i + 1) / (steps.length + 1)) * Math.max(horizon, 6)));
    const target = Math.max(1, Math.round(plan.perMonth[m] * Math.max(1, months - 2)));
    out.push({ dueDate: addMonths(start, months), metric: m, target, label: `${n0(target)} ${METRIC_NOUN[m]} by month ${months}` });
  });
  if (plan.annual.deals_done > 0) {
    const months = Math.max(horizon, 1);
    out.push({ dueDate: addMonths(start, months), metric: 'deals_done', target: 1, label: `First deal done by month ${months}` });
  }
  return out.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

// ---------------------------------------------------------------------------
// Periods and pacing (spec 7.1–7.3): week starts Monday; calendar quarter/year (or a fiscal start
// month); pro-rated by working days (Mon–Fri); a goal starting mid-period is pro-rated too.
// ---------------------------------------------------------------------------

export type Period = 'week' | 'month' | 'quarter' | 'ytd';

export function periodBounds(period: Period, today: string, fiscalStartMonth = 1): { from: string; to: string } {
  const d = new Date(`${today}T12:00:00Z`);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const iso = (yy: number, mm: number, dd: number) => new Date(Date.UTC(yy, mm - 1, dd, 12)).toISOString().slice(0, 10);
  if (period === 'week') {
    const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
    return { from: addDays(today, -dow), to: addDays(today, 6 - dow) };
  }
  if (period === 'month') return { from: iso(y, m, 1), to: addDays(iso(y, m + 1, 1), -1) };
  // Fiscal year containing today.
  const fyStartYear = m >= fiscalStartMonth ? y : y - 1;
  const fyStart = iso(fyStartYear, fiscalStartMonth, 1);
  if (period === 'ytd') return { from: fyStart, to: addDays(iso(fyStartYear + 1, fiscalStartMonth, 1), -1) };
  const monthsIn = (m - fiscalStartMonth + 12) % 12;
  const qStart = addMonths(fyStart, Math.floor(monthsIn / 3) * 3);
  return { from: qStart, to: addDays(addMonths(qStart, 3), -1) };
}

export function workingDays(from: string, to: string): number {
  let n = 0;
  for (let t = Date.parse(`${from}T12:00:00Z`); t <= Date.parse(`${to}T12:00:00Z`); t += 86_400_000) {
    const dow = new Date(t).getUTCDay();
    if (dow !== 0 && dow !== 6) n++;
  }
  return n;
}

/** Annual target → this period's target, counting only working days on or after the goal start. */
export function periodTarget(annual: number, workingWeeks: number, bounds: { from: string; to: string }, goalStart: string): number {
  const from = goalStart > bounds.from ? goalStart : bounds.from;
  if (from > bounds.to) return 0;
  const perDay = annual / (Math.max(1, workingWeeks) * 5);
  return perDay * workingDays(from, bounds.to);
}

export type Pace = 'ahead' | 'on_pace' | 'behind' | 'ramping';

/**
 * Pacing against the pro-rated target for the elapsed part of the period (spec 7.1).
 * Revenue lags: before the time-to-income horizon it shows "ramping", never "behind" (spec 5.4).
 */
export function pace(
  actual: number,
  periodTargetValue: number,
  bounds: { from: string; to: string },
  today: string,
  goalStart: string,
  opts: { lagging?: boolean; rampEnds?: string; todayFraction?: number } = {},
): Pace {
  if (opts.lagging && opts.rampEnds && today < opts.rampEnds) return 'ramping';
  const from = goalStart > bounds.from ? goalStart : bounds.from;
  const total = workingDays(from, bounds.to);
  // Working days before today, plus however much of today's workday has passed (spec 8.1 example:
  // Wednesday 11am counts Mon, Tue and part of Wed).
  const last = today < bounds.to ? today : bounds.to;
  const isWorkday = workingDays(last, last) === 1;
  const before = workingDays(from, last) - (isWorkday ? 1 : 0);
  const elapsed = before + (isWorkday && today <= bounds.to ? (opts.todayFraction ?? 1) : 0);
  const expected = total > 0 ? periodTargetValue * Math.min(1, elapsed / total) : 0;
  if (expected <= 0) return 'on_pace';
  const ratio = actual / expected;
  return ratio >= 1.1 ? 'ahead' : ratio >= 0.9 ? 'on_pace' : 'behind';
}

/** How much of the workday (8am–6pm) has passed at this hour, 0–1. */
export const workdayFraction = (hour: number) => Math.min(1, Math.max(0, (hour - 8) / 10));

export { addMonths };
